import { useEffect, useRef } from 'react';
import type { GameActions } from '../app/connection';
import { seconds, useRemaining } from '../app/clock';
import { sfx } from '../audio/sfx';
import { ClueRenderer } from '../clue/ClueRenderer';
import { AnswerRenderer } from '../input/AnswerRenderer';
import type { GameState } from '../app/state';
import { playerName, seatIndex } from '../app/state';
import { Avatar } from '../ui/Avatar';
import { Countdown } from '../ui/Countdown';
import { OddsBadge } from '../ui/OddsBadge';
import { Seat } from '../ui/Seat';
import { cx } from '../ui/cx';
import { delta as fmtDelta, miniGameLabel } from '../ui/format';
import s from './screens.module.scss';

/**
 * La manche.
 *
 * Trois choses a l'ecran, et l'ordre compte : la cote (ce qu'on perd a
 * attendre), l'indice (ce qu'on gagne a attendre), l'entree (la sortie). Tout
 * le jeu tient dans cette tension, l'ecran ne doit rien y ajouter.
 *
 * Les deux registres se rejoignent ici, et c'est la seule ligne de tout le
 * client qui sait qu'un mini-jeu existe :
 *
 *     <ClueRenderer payload={...} />   ce qu'on percoit
 *     <AnswerRenderer spec={...} />    ce qu'on manipule
 *
 * Ni l'un ni l'autre ne connait `lyrics`, `color` ou `year`. Ajouter un
 * mini-jeu au serveur ne change donc PAS ce fichier.
 */
export interface RoundScreenProps {
  state: GameState;
  actions: GameActions;
}

export function RoundScreen({ state, actions }: RoundScreenProps) {
  const round = state.round;
  const remaining = useRemaining(state.phaseEndsAt);
  // Meme chrono que le Countdown affiche, reutilise pour les indices qui se
  // revelent en continu (l'image) plutot que par palier de cote.
  const totalMs = state.phaseTotalMs ?? 0;
  const elapsedMs = Math.max(0, totalMs - remaining);
  const buzzRemaining = useRemaining(state.buzzEndsAt);

  // Un souffle a chaque nouveau palier — jamais au premier rendu du tout
  // (la ref demarre alignee), sinon la manche sonnerait des son ouverture.
  const lastStage = useRef(state.stageToken);
  useEffect(() => {
    if (state.stageToken !== lastStage.current) sfx.reveal();
    lastStage.current = state.stageToken;
  }, [state.stageToken]);

  // Le verdict de SA PROPRE reponse. Celui des autres reste inconnu du
  // client avant la resolution — impossible d'y jouer un son en avance.
  const feedbackToken = state.feedback?.token;
  useEffect(() => {
    if (feedbackToken === undefined) return;
    (state.feedback?.correct ? sfx.correct : sfx.wrong)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackToken]);

  // Quelqu'un prend la main au buzzer — soi-meme ou un adversaire, le claquement compte.
  const lastBuzzHolder = useRef<string | null>(null);
  useEffect(() => {
    const holder = round?.buzzHolder ?? null;
    if (holder !== null && holder !== lastBuzzHolder.current) sfx.buzz();
    lastBuzzHolder.current = holder;
  }, [round?.buzzHolder]);

  if (!round) return null;

  const me = round.players.find((player) => player.playerId === state.you);
  const stake = me?.stake ?? 0;
  const locked = state.submitted || round.phase === 'ended';
  const feedback = state.feedback;

  // Trois situations propres au buzzer, mutuellement exclusives : j'ai la
  // main (je reponds normalement), quelqu'un d'autre l'a (j'attends), ou
  // personne ne l'a (je peux tenter le buzz si je suis encore en lice).
  const iHoldBuzz = round.buzzer && round.buzzHolder === state.you;
  const someoneElseHoldsBuzz = round.buzzer && round.buzzHolder !== null && !iHoldBuzz;
  const canBuzz = round.buzzer && round.buzzHolder === null && (me?.status ?? 'thinking') === 'thinking';
  const eliminated = round.buzzer && (me?.status ?? 'thinking') !== 'thinking' && !iHoldBuzz;

  return (
    <main className={cx(s.round, feedback && (feedback.correct ? s.roundGood : s.roundBad))}>
      <header className={s.roundHeader}>
        <span className={s.roundTag}>
          Manche {state.roundNumber} / {state.totalRounds} · {miniGameLabel(round.miniGame)}
        </span>
        <Countdown remainingMs={remaining} totalMs={state.phaseTotalMs} />
      </header>

      <div className={s.roundBody}>
        <aside className={s.roundSide}>
          <OddsBadge
            value={round.odds}
            stageToken={state.stageToken}
            potential={stake > 0 ? stake * round.odds - stake : undefined}
          />
          <div className={s.roundStake}>
            <span className={s.roundStakeLabel}>Ta mise</span>
            <strong className={s.roundStakeValue}>{stake}</strong>
          </div>
        </aside>

        <section className={s.stage}>
          <h1 className={s.prompt}>{round.prompt}</h1>

          {/* REGISTRE 1 : la forme de l'indice decide du composant. */}
          <div className={s.clue}>
            <ClueRenderer
              payload={round.payload}
              stageToken={state.stageToken}
              elapsedMs={elapsedMs}
              totalMs={totalMs}
            />
          </div>

          {/* REGISTRE 2 : la forme de l'entree decide du widget. La cle par
              manche remonte le widget a neuf — mais PAS a chaque palier, pour
              que le QCM garde la memoire des options deja eliminees. */}
          <div className={s.answer}>
            {feedback ? (
              <div className={cx(s.verdict, feedback.correct ? s.verdictGood : s.verdictBad)}>
                <strong className={s.verdictHead}>{feedback.correct ? 'Juste' : 'Rate'}</strong>
                <span className={s.verdictDelta}>{fmtDelta(feedback.delta)}</span>
                {feedback.detail ? <span className={s.verdictDetail}>{feedback.detail}</span> : null}
              </div>
            ) : someoneElseHoldsBuzz ? (
              // Quelqu'un d'autre a la main : reproduire le widget de reponse
              // ici mentirait, puisque toute soumission serait de toute facon
              // refusee cote serveur. On montre plutot QUI repond et combien
              // de temps il lui reste, pour que l'attente reste lisible.
              <div className={s.buzzWaiting}>
                <span className={s.buzzWaitingLabel}>
                  {playerName(state, round.buzzHolder!)} a la main
                </span>
                <span className={s.buzzWaitingClock}>{seconds(buzzRemaining)}s pour repondre</span>
              </div>
            ) : eliminated ? (
              <div className={s.buzzOut}>Tu as deja tente ta chance sur cette manche.</div>
            ) : canBuzz ? (
              <button type="button" className={s.buzzButton} onClick={actions.buzz}>
                BUZZ
              </button>
            ) : (
              <AnswerRenderer
                key={state.roundToken}
                spec={round.input}
                disabled={locked}
                onSubmit={actions.answer}
              />
            )}
          </div>
        </section>

        <aside className={s.roundSide}>
          <h2 className={s.panelTitle}>A table</h2>
          <ul className={s.roundSeats}>
            {round.players.map((player) => {
              const info = state.players.find((p) => p.id === player.playerId);
              return (
                <li key={player.playerId} className={s.roundSeatRow}>
                  <Avatar
                    avatarId={info?.avatarId}
                    colorId={info?.colorId}
                    seatIndex={seatIndex(state, player.playerId)}
                    size="sm"
                  />
                  <Seat
                    className={player.playerId === round.buzzHolder ? s.seatBuzzing : undefined}
                    name={playerName(state, player.playerId)}
                    seatIndex={seatIndex(state, player.playerId)}
                    colorId={info?.colorId}
                    bot={info?.bot ?? false}
                    you={player.playerId === state.you}
                    offline={info ? !info.connected : false}
                    status={player.status}
                    right={
                      player.playerId === round.buzzHolder ? (
                        <span className={s.buzzTag}>buzz</span>
                      ) : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
          {/* On montre QUI a repondu et si c'etait juste — jamais QUOI. Le
              serveur ne l'envoie d'ailleurs pas : la reponse des autres
              n'existe pas cote client avant la resolution. */}
        </aside>
      </div>
    </main>
  );
}
